import { useEffect } from 'react';

/**
 * PUT EVERY `sv2pulse` ON ONE CLOCK.
 *
 * The pulsing marks — the blue "updated" node and its tick, the amber "under
 * review" node and its chip — all run the same 1.2s `sv2pulse`. But a CSS
 * animation's phase is fixed when its element MOUNTS, and these mount at
 * different moments: a legend chip is there immediately, a row's mark waits for
 * its row, and react-flow paints its nodes asynchronously after that.
 *
 * Measured on the dashboard before this existed: 11 pulses running on two
 * different clocks, 473ms and 1556ms apart. On a 1200ms cycle that is a 1083ms
 * offset — very nearly antiphase, so half the marks were dark while the other
 * half were lit, and the page read as flickering rather than beating.
 *
 * THERE IS NO CSS FIX. `animation-delay` is relative to each element's own
 * start, so it cannot pull an element that mounted late back into step. The Web
 * Animations API can: every animation shares the document timeline, so giving
 * them all the same `startTime` puts them on the same clock, and a mark that
 * appears later joins mid-beat instead of starting its own.
 *
 * Re-aligns only when the SET of animations changes. `getAnimations()` walks
 * every animation in the document, and re-aligning ones already in step would
 * restart them — producing exactly the stutter this exists to remove.
 *
 * Reduced motion needs no special case: the animations are not created at all,
 * so there is nothing to synchronise.
 */
export const useSyncedPulse = (animationName = 'sv2pulse') => {
	useEffect(() => {
		if (typeof document === 'undefined' || !document.getAnimations) return;
		let known = -1;
		let queued = false;

		const sync = () => {
			queued = false;
			const pulses = document
				.getAnimations()
				.filter((a: any) => a?.animationName === animationName);
			if (pulses.length === known) return;
			known = pulses.length;
			pulses.forEach((a: any) => {
				try {
					a.startTime = 0;
				} catch {
					/* not on a timeline yet — the next pass catches it */
				}
			});
		};

		const schedule = () => {
			if (queued) return;
			queued = true;
			requestAnimationFrame(sync);
		};

		schedule();
		const mo = new MutationObserver(schedule);
		mo.observe(document.body, { childList: true, subtree: true });
		/* Backstop: react-flow can swap a node's styles without a DOM mutation
		   the observer sees. */
		const t = setInterval(schedule, 2000);
		return () => {
			mo.disconnect();
			clearInterval(t);
		};
	}, [animationName]);
};
